import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBolt } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ActionStatusChip, actionHintKey } from "./actionParts";
import { type ActionListPayload, parseActionListPayload } from "./chiefSchema";

interface ActionListRendererProps {
  block: StreamingBlock;
}

export const ActionListRenderer = (props: ActionListRendererProps) => {
  const { t } = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ActionListPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseActionListPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("chief_actions_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().actions.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBolt class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{t("chief_actions_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().actions.length === 1
                    ? t("chief_actions_card.results_one")
                    : t("chief_actions_card.results", { count: String(data().actions.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().actions}>
                  {(action) => (
                    <li class="px-3 py-2">
                      <div class="flex items-center justify-between gap-3">
                        <span class="truncate text-sm text-primary-text">{action.name}</span>
                        <ActionStatusChip enabled={action.enabled} />
                      </div>
                      <Show when={action.description}>
                        <p class="mt-0.5 truncate text-xs text-muted-text">{action.description}</p>
                      </Show>
                      <Show when={actionHintKey(action)}>
                        {(key) => <p class="mt-0.5 text-xs text-muted-text">{t(key())}</p>}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
